// apparentScale.ts — Keep a body at least a minimum apparent (on-screen) size as
// the camera dollies away, so it stays visible/clickable while still rendering at
// true scale up close. The floor is mode-dependent: focused entities use a LARGE
// floor, galaxy-map stars a smaller one, and receded background bodies none —
// giving each map mode (galaxy / system / planet) its own visual hierarchy. All
// bodies (stars, planets, moons) run through this one function for consistency.
import type { Camera, Vector3 } from 'three';

// Floor as a fraction of distance: rendered radius is at least dist * floor, i.e.
// a roughly constant on-screen size when far away. Stars anchor the hierarchy;
// planets are pinned to a third of the star's floor so a system reads star-first
// and planets never bloat to overlap it, and moons a fraction of that again.
export const FLOOR_STAR = 0.02; // the focused system's star — big and clear
export const FLOOR_STAR_GALAXY = 0.004; // stars on the galaxy map — small dots that don't crowd
export const FLOOR_PLANET = FLOOR_STAR * 0.33; // planets ≤ 1/3 the star's min size
export const FLOOR_MOON = FLOOR_PLANET * 0.45; // moons smaller again
export const FLOOR_SHIP = FLOOR_PLANET * 0.7; // ships hold a clearly-visible minimum size
export const FLOOR_NONE = 0; // receded background — true scale

export function apparentScale(
  camera: Camera,
  worldPos: Vector3,
  trueRadius: number,
  floor: number,
): number {
  const minRadius = camera.position.distanceTo(worldPos) * floor;
  return minRadius > trueRadius ? minRadius / trueRadius : 1;
}
